'use client'

import { useEffect, useRef, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { useViewportScale, transformPresets } from 'src/hooks/useViewportScale'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import { BirdieAutoBetSeg, type BirdieSegItem } from './BirdieAutoBetSeg'
import { BoardHeadIcon } from './BoardHeadIcon'
import { BirdieBallDefs, GolfBall } from './GolfBall'
import { RoundMark } from './RoundMark'
import { autoBetZoneText, birdieAutoBetCopy } from './autoBetCopy'
import {
  AXIS_TICKS,
  ZONE_PRESETS,
  axisPos,
  axisValue,
  ceilingForOptions,
  optionsForCeiling,
  presetFor,
  stepOptions,
  stepRounds,
  type AutoBetPlan,
  type AutoBetRule,
  type ZoneBounds,
  type ZoneMarkets,
  type ZonePreset
} from './birdieAutoBet'
import { chipStyleAt } from './chipPalette'
import type { BirdieTileView, Translate } from './tileViewModel'

export type BirdieAutoBetSheetProps = Readonly<{
  draft: AutoBetRule
  onDraftChange: (next: AutoBetRule) => void
  /**
   * This round's priced tiles, as the axis ticks — drawn only alongside a
   * `preview`, which is what can light them. A locked round's tiles are still
   * here (the board is still drawing them), and ticks that cannot light read
   * as a zone that catches nothing.
   */
  tiles: readonly BirdieTileView[]
  /**
   * What the draft would place on the open round, which lights the ticks it
   * would catch; `null` when none is open. The planner's answer and not a
   * second reading of the zone, so a lit tick is a tile the rule would really
   * take — bettable, priced, and inside the round's own limits.
   */
  preview: AutoBetPlan | null
  /** The chip ladder, display units. */
  chipValues: readonly number[]
  formatAmount: (amount: number) => string
  /** '3.00x'. */
  formatMultiplier: (m: number) => string
  armed: boolean
  onStart: () => void
  /** Armed only: the running rule takes the draft's settings. */
  onUpdate: () => void
  onStop: () => void
  onClose: () => void
  /** Where focus goes when the element that opened the sheet is gone by the time it closes. */
  onRestoreFocus: () => void
  t: Translate
}>

const TITLE_ID = 'birdie-autobet-sheet-title'

/**
 * The slider's resolution. A thousand steps on a log track is a step of 0.49%
 * of the price anywhere on it — COARSER than the two decimals a bound is kept
 * to for every price above ~2.04x (the steps either side of 10x are 9.96 and
 * 10.01), and finer below it, where a few prices are reached by two adjacent
 * steps. `fromStep` puts the preset edges back on the track.
 */
const AXIS_STEPS = 1000

/**
 * The four named zones, as shortcuts that move the handles. There is no fifth
 * chip: the slider is the control now (owner 2026-09-17), and a band set by
 * hand simply lights none of these — `presetFor` judges by the bounds alone,
 * so a drag that lands exactly on a preset's edges lights it again for free.
 * `PRESET_BOUND_AT_STEP` is what lets a handle land on those edges at all.
 */
const PRESET_ORDER: readonly Exclude<ZonePreset, 'custom'>[] = ['short', 'middle', 'long', 'far']
const MARKET_ORDER: readonly ZoneMarkets[] = ['exact', 'count', 'both']

/** The two repeats, in the board's own order. Their marks are the tiles' own. */
const ROUND_TYPES = [
  { side: 'frost', kind: 'FROST' },
  { side: 'fire', kind: 'FIRE' }
] as const

/** Reference px — what the board's count grid draws a round tile's mark at. */
const MARK_PX = 22

export const toStep = (multiplier: number): number => Math.round(axisPos(multiplier) * AXIS_STEPS)

/**
 * The preset edges, by the step each one sits on. Without this the track skips
 * 10 (9.96 → 10.01) and 40 (39.79 → 40.18), so three of the four chips could
 * never be re-lit by putting a handle back — and a band that looked exactly
 * like the preset would quietly leave the round's 10.00x option outside the
 * zone. Each edge rounds into its own step's cell, so the track stays ordered.
 */
const PRESET_BOUND_AT_STEP = new Map<number, number>(
  PRESET_ORDER.flatMap((name) => {
    const { min, max } = ZONE_PRESETS[name]
    return (max === null ? [min] : [min, max]).map((bound) => [toStep(bound), bound] as const)
  })
)

/** A step back to a price, at the two decimals the label shows — so a bound IS the number the player reads. */
export const fromStep = (step: number): number =>
  PRESET_BOUND_AT_STEP.get(step) ?? Math.round(axisValue(step / AXIS_STEPS) * 100) / 100

const pct = (pos: number): string => `${(pos * 100).toFixed(3)}%`

const on = (base: string, lit: boolean): string => (lit ? `${base} ${base}--on` : base)

/**
 * A section's place in the stack, as the stagger its entry reads off. One
 * custom property rather than six rules: the sheet rises block by block, and
 * the reduced-motion block at the foot of the stylesheet stops all of it.
 */
const rise = (index: number): CSSProperties => ({ '--rise': String(index) }) as CSSProperties

export type SplitLine = Readonly<{ before: string; match: string; after: string }>

/**
 * A translated sentence cut around the sum it carries, so the SUM can be drawn
 * at a size the words around it are not — which is what the safety line above
 * the arm button needed (owner 2026-09-18: nobody should be able to overlook
 * how much a round can stake). The needle is the formatted amount the caller
 * has just put INTO the sentence, so the cut is exact and takes the locale's
 * own word order with it: a language that leads with the money splits the same
 * way, since the split is by position and not by pattern.
 *
 * No copy is added or changed by this — `before + match + after` is the
 * sentence, character for character. A sentence the amount is somehow not
 * inside comes back whole in `before`, and is drawn as one plain line rather
 * than losing a word.
 */
export function splitAround(sentence: string, needle: string): SplitLine {
  const at = needle === '' ? -1 : sentence.indexOf(needle)
  if (at < 0) return { before: sentence, match: '', after: '' }
  return {
    before: sentence.slice(0, at),
    match: needle,
    after: sentence.slice(at + needle.length)
  }
}

/**
 * The open end of both ladders, drawn rather than written. Not the bare ∞
 * character: the board's display face draws it hairline-thin, and this sits
 * between two arrows at the weight the round marks are stroked at.
 */
function InfinityGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="birdie-autobet-sheet__infinity"
      fill="none"
      focusable="false"
      viewBox="0 0 28 16"
    >
      <path
        d="M14 8c2.5-3.5 4.2-5 7-5a5 5 0 0 1 0 10c-2.8 0-4.5-1.5-7-5S9.8 3 7 3a5 5 0 0 0 0 10c2.8 0 4.5-1.5 7-5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.2"
      />
    </svg>
  )
}

export type StepperProps = Readonly<{
  label: string
  /** The one value on show — a stepper says where you are, not where you could go. */
  value: string
  /**
   * The top of the ladder — no limit, until I stop. The slot draws an ∞ and
   * `value` goes beside it visually hidden rather than printed: the sentence
   * is long in sixteen locales and was ellipsised in four of them inside a
   * 124-px box, which also made the two steppers different widths.
   */
  unlimited: boolean
  lessLabel: string
  moreLabel: string
  /**
   * `null` at the end of the ladder: the arrow is dead, but still reachable —
   * `aria-disabled` and not the native attribute, the board's own answer for
   * the locked tile. A focused control that goes natively disabled is
   * focus-fixed to `<body>`, and the Escape and Tab listener below is bound to
   * the dialog, so the very press that reaches the last rung would take the
   * keyboard out of the sheet. Reachable also lets the arrow say so.
   */
  onLess: (() => void) | null
  onMore: (() => void) | null
}>

/** The ± pair: two arrows around a reading, the shape the owner asked both counts to take. */
export function Stepper({
  label,
  value,
  unlimited,
  lessLabel,
  moreLabel,
  onLess,
  onMore
}: StepperProps) {
  return (
    <div aria-label={label} className="birdie-autobet-sheet__stepper" role="group">
      <button
        aria-disabled={onLess === null}
        aria-label={lessLabel}
        className="birdie-autobet-sheet__step"
        onClick={onLess ?? undefined}
        type="button"
      >
        <span aria-hidden="true">−</span>
      </button>
      {/* Live: the group's name is the field and the arrows' names are fixed,
          so the reading is the only thing a step changes — and it is not
          focusable, so a player working the arrows never meets it otherwise.
          At the top of the ladder the glyph is decorative and the sentence
          sits beside it hidden: a region announces its CONTENT, and a name on
          the region is not reliably spoken when it mutates — so the step that
          reaches the last rung would be the silent one. The title stays as the
          crutch for a reading wider than its box. */}
      <span
        aria-live="polite"
        className="birdie-autobet-sheet__step-value"
        role="status"
        title={value}
      >
        {unlimited ? <InfinityGlyph /> : null}
        {unlimited ? <span className="birdie-sr-only">{value}</span> : value}
      </span>
      <button
        aria-disabled={onMore === null}
        aria-label={moreLabel}
        className="birdie-autobet-sheet__step"
        onClick={onMore ?? undefined}
        type="button"
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  )
}

/**
 * The auto-bet sheet (B32) — the `BirdieRoundSheet` dialog pattern with the
 * board's own controls in it. Presentational: the rule is the parent's, every
 * change goes back through `onDraftChange`, and what the draft would do to the
 * open round arrives as `preview`. No state of its own beyond the refs the
 * focus trap needs.
 *
 * It was a settings form — six stacked fields, two stake ladders, a row of
 * money chips for the ceiling — and it read as one (owner 2026-09-17). It is
 * now the board's own vocabulary: real CHIPS for the stake, landing in a zone
 * that is the stake for everything the rule places; the ceiling and the count
 * as ± steppers, which is one reading each instead of a row of six; and the
 * two repeats as round TILES carrying the board's flake and flame, not as
 * switches with a stake row under them.
 *
 * Two of those are worth spelling out. The ceiling is stored as money and set
 * as a COUNT of options (`ceilingForOptions` / `optionsForCeiling`): every
 * placement takes the same stake, so "up to ten options" is the question, and
 * a count needs no currency to make sense of. What that count costs is said
 * once, in the line above the button, and nowhere else.
 * And the stake is one number: moving it moves the ceiling with it, or the
 * round's ceiling would silently tighten as the chip grew.
 *
 * THE SLIDER IS THE ZONE CONTROL (owner 2026-09-17). It is always on show,
 * with this round's prices as ticks and the ones the rule would take lit; the
 * four named chips are shortcuts that move the handles, and a band set by hand
 * lights none of them. There is no Custom chip — it unfolded the control the
 * player was already looking at.
 *
 * The two handles are two native ranges over one track: the track ignores
 * the pointer and each thumb takes it, so either can be grabbed wherever it
 * is, and the upper input is drawn in front unless both sit together in the
 * right half — where the lower one is the one a player will want next.
 *
 * B32b — THE VISUAL PASS (owner 2026-09-18: "could have more of a game feel to
 * it … striking visuals, animations and just very polished"). Nothing about the
 * rule, the props or the behaviour moved; what changed is what the sheet looks
 * like it is made of:
 *
 *  - every block is an INSET PANEL — a well carved into the dialog's glass —
 *    so the sheet reads as a game's settings panel rather than as a form;
 *  - the four zones and the three markets are one SWITCH SET each
 *    (`<BirdieAutoBetSeg>`): a recessed track with a gold thumb that travels
 *    to the checked segment, full width and equal segments in both rows;
 *  - the markets carry the BOARD's own column-header pictograms, so the row
 *    that chooses between them is drawn in the same two icons the board heads
 *    its columns with — and Both carries the pair, overlapped, because Both IS
 *    the two of them and an empty third tab would read as an absence;
 *  - the two repeats are colourless until they are ON — the mark is neutral
 *    and the tile is recessed, and the family's ice or ember arrives with the
 *    press, which is also announced by a lit pip and by the tile standing up;
 *  - the SLIDER HANDLES are the board's real golf ball (`<GolfBall>`) in a
 *    gold cup ring, drawn over transparent native thumbs so every keyboard
 *    contract the two ranges carry is exactly the one that shipped;
 *  - and the safety line is a READOUT: the sum the ceiling can cost is cut out
 *    of its own sentence (`splitAround`) and set large in gold — amber, when
 *    the ladder is at its open end and there is no ceiling at all.
 *
 * Portalled to `<body>` and scaled like the round sheet, for the same reason:
 * every ancestor it could otherwise have is transformed.
 */
export function BirdieAutoBetSheet({
  draft,
  onDraftChange,
  tiles,
  preview,
  chipValues,
  formatAmount,
  formatMultiplier,
  armed,
  onStart,
  onUpdate,
  onStop,
  onClose,
  onRestoreFocus,
  t
}: BirdieAutoBetSheetProps) {
  const copy = birdieAutoBetCopy(t)
  const dialogRef = useRef<HTMLDivElement>(null)
  // Read through a ref so the focus effect below still runs once, on mount.
  const restoreRef = useRef(onRestoreFocus)
  restoreRef.current = onRestoreFocus

  const scaleStyle = useViewportScale({
    transform: transformPresets.basic,
    transformOrigin: 'center center'
  })

  // Focus management: remember the opener, focus the dialog, restore on
  // unmount. The opener may be GONE by then — Stop unmounts the pill that
  // opened this sheet in the same commit, and a rule that stops itself
  // (finished, a hand on the board, the balance) unmounts it under an open
  // sheet — and `focus()` on a detached node is a no-op that drops focus to
  // `<body>`. An opener no longer in the document hands focus to the host.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    return () => {
      if (opener !== null && opener.isConnected) opener.focus()
      else restoreRef.current()
    }
  }, [])

  // Escape closes; Tab cycles inside the dialog. A native listener rather than
  // a JSX handler on the dialog, as the round sheet explains.
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      // Nothing in here is natively disabled any more — the dead stepper
      // arrows carry `aria-disabled` and stay in the cycle on purpose. The
      // exclusion stays as the guard against the next control that is.
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input, [href], [tabindex]:not([tabindex="-1"])'
      )
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (first === undefined || last === undefined) return
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    dialog.addEventListener('keydown', onKeyDown)
    return () => {
      dialog.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  if (typeof document === 'undefined') return null

  const zone = draft.zone
  const zoneText = autoBetZoneText(copy, zone, formatMultiplier)
  const minStep = toStep(zone.min)
  const maxStep = zone.max === null ? AXIS_STEPS : toStep(zone.max)
  const minOnTop = minStep >= maxStep && minStep > AXIS_STEPS / 2

  const setZone = (bounds: ZoneBounds, preset: ZonePreset): void => {
    onDraftChange({ ...draft, zone: { preset, min: bounds.min, max: bounds.max } })
  }

  const choosePreset = (preset: Exclude<ZonePreset, 'custom'>): void => {
    setZone(ZONE_PRESETS[preset], preset)
  }

  const moveMin = (step: number): void => {
    let min = fromStep(Math.min(step, maxStep))
    if (zone.max !== null && min > zone.max) min = zone.max
    const bounds = { min, max: zone.max }
    setZone(bounds, presetFor(bounds))
  }

  const moveMax = (step: number): void => {
    const clamped = Math.max(step, minStep)
    let max: number | null = clamped >= AXIS_STEPS ? null : fromStep(clamped)
    if (max !== null && max < zone.min) max = zone.min
    const bounds = { min: zone.min, max }
    setZone(bounds, presetFor(bounds))
  }

  // The ceiling, read as the count it was set as. The money is derived from it
  // and the stake on every render, so the two can never drift apart.
  const maxOptions = optionsForCeiling(draft.stake, draft.roundCeiling)
  const setMaxOptions = (options: number): void => {
    onDraftChange({ ...draft, roundCeiling: ceilingForOptions(draft.stake, options) })
  }
  const pickStake = (stake: number): void => {
    onDraftChange({ ...draft, stake, roundCeiling: ceilingForOptions(stake, maxOptions) })
  }
  /** A stepper arrow, or `null` where the ladder has nowhere left to go. */
  const stepTo = (
    next: number | null,
    current: number | null,
    apply: (value: number | null) => void
  ): (() => void) | null =>
    next === current
      ? null
      : () => {
          apply(next)
        }

  /**
   * The options ladder has no open end since it tops out at every option there
   * is, so its arrows are their own helper rather than borrowing the rounds
   * ladder's nullable one. `null` still means "this arrow has nowhere to go".
   */
  const stepOptionsTo = (direction: 1 | -1): (() => void) | null => {
    const next = stepOptions(maxOptions, direction)
    return next === maxOptions
      ? null
      : () => {
          setMaxOptions(next)
        }
  }

  const presetItems: readonly BirdieSegItem<Exclude<ZonePreset, 'custom'>>[] = PRESET_ORDER.map(
    (preset) => ({
      value: preset,
      label: copy.presets[preset]
    })
  )

  /**
   * The markets row wears the BOARD's own column headings (owner 2026-09-18:
   * "a little icon beside exact order and count like the ones we use on the
   * labels"), so the tab that picks a market and the heading over that market's
   * column are the same pictogram — the pin for exact order, the two balls for
   * count.
   *
   * BOTH gets BOTH icons, overlapped. It is not a third market: it is those two
   * together, and the pair says so without a word. The alternative — no icon at
   * all — would make the one tab that means "everything" the only one with
   * nothing on it, which reads as an absence rather than a sum, and would leave
   * the row visually ragged on a control whose whole point is three equal tabs.
   */
  const marketItems: readonly BirdieSegItem<ZoneMarkets>[] = MARKET_ORDER.map((markets) => ({
    value: markets,
    // The tab says EXACT, not "exact order" (owner 2026-09-18). Its own short
    // key, so the board's column heading — which is the same two words in
    // sixteen locales and is borrowed, not Birdie's to rename — is untouched.
    label: markets === 'exact' ? copy.marketExactShort : copy.markets[markets],
    icon:
      markets === 'both' ? (
        <>
          <BoardHeadIcon column="exact" />
          <BoardHeadIcon column="count" />
        </>
      ) : (
        <BoardHeadIcon column={markets} />
      )
  }))

  const stakeStyle = chipStyleAt(chipValues.indexOf(draft.stake))

  // What the draft would put on this round, by option — the planner's answer,
  // not a second reading of the zone. Lights the ticks it would take, whatever
  // the plan's skip: a round the ceiling refused still had a zone.
  const caughtIds = new Set<string>(
    preview === null ? [] : preview.placements.map((placement) => placement.optionId)
  )
  // The tick layer belongs to the PREVIEW, so it goes when the preview does.
  // The board's own opener is on screen only while a round is LOCKED, and the
  // locked round's tiles are still priced and still here — drawn without a
  // preview to light any of them, a full row of grey dots says "your zone
  // catches nothing" on the one flow the opener exists for. The gold band
  // stands alone until a round opens, and the zone is editable either way.
  // The round-type pair is never on the axis whatever the round: it is priced
  // from the seed, not from the golfer, and has its own tiles below.
  const axisTiles = preview === null ? [] : tiles.filter((tile) => tile.kind !== 'round')

  // The sheet's one figure of money: what the ceiling's option count costs at
  // the chosen stake. Derived from the count on every render, like the count
  // itself, so the line and the rule cannot drift apart — and at the top of
  // the ladder there is no sum to quote, so it says the ladder's own word.
  const ceilingText =
    maxOptions === null ? copy.noLimit : formatAmount(ceilingForOptions(draft.stake, maxOptions))
  const ceilingLine = splitAround(copy.maxStakePerRound(ceilingText), ceilingText)

  const sheet = (
    // Stops an ancestor's outside-click listener from seeing a click that
    // landed inside this dialog (the round sheet's own guard); not an
    // interaction — every control in here is a button or an input.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- see above
    <div
      className="birdie-autobet-sheet"
      onMouseDown={(event) => {
        event.stopPropagation()
      }}
    >
      <button
        aria-label={copy.close}
        className="birdie-autobet-sheet__backdrop"
        onClick={onClose}
        tabIndex={-1}
        type="button"
      />

      {/*
        The slider handles are the board's real ball, and a ball paints from
        two gradients declared by `<BirdieBallDefs>`. This sheet is portalled to
        `document.body`, OUTSIDE the board's overlay, so it cannot borrow the
        board's copy — and declaring them again is free and safe whether or not
        the board is on the same page: the ids are fixed and the definitions
        identical, so a duplicate binds every reference to the first one, which
        is the same drawing (`GolfBall.tsx`). The round sheet does exactly this.
      */}
      <BirdieBallDefs />

      <div className="birdie-autobet-sheet__scale" style={scaleStyle}>
        <div
          aria-labelledby={TITLE_ID}
          aria-modal="true"
          className="birdie-autobet-sheet__dialog"
          ref={dialogRef}
          role="dialog"
          tabIndex={-1}
        >
          <button
            aria-label={copy.close}
            className="birdie-autobet-sheet__close"
            onClick={onClose}
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>

          <div className="birdie-autobet-sheet__scroll">
            <div className="birdie-autobet-sheet__head">
              <h2 className="birdie-autobet-sheet__title" id={TITLE_ID}>
                {copy.title}
              </h2>
            </div>

            {/* ── Zone: the switch set, the slider under it, the live range in the header ── */}
            <div className="birdie-autobet-sheet__field" style={rise(0)}>
              <div className="birdie-autobet-sheet__label">
                <span>{copy.zoneLabel}</span>
                <span className="birdie-autobet-sheet__value">{zoneText}</span>
              </div>
              {/* A band dragged by hand is none of the four, so nothing is
                  checked and the thumb retracts — `presetFor` already judges
                  by the bounds alone, and this is that answer, drawn. */}
              <BirdieAutoBetSeg
                items={presetItems}
                label={copy.zoneLabel}
                onPick={choosePreset}
                value={zone.preset === 'custom' ? null : zone.preset}
              />

              {/*
                The two ranges come FIRST now and everything drawn comes after
                them, which is what lets the drawing answer the inputs' own
                states: the band stops easing while a thumb is being dragged,
                and the grabbed handle swells, both off a plain sibling
                selector. Every drawn layer ignores the pointer, so the thumbs
                are still the only things in here that can be grabbed.
              */}
              <div className="birdie-autobet-axis">
                <input
                  aria-label={copy.ariaMin}
                  aria-valuetext={formatMultiplier(zone.min)}
                  className={`birdie-autobet-axis__range birdie-autobet-axis__range--min${minOnTop ? ' birdie-autobet-axis__range--front' : ''}`}
                  max={AXIS_STEPS}
                  min={0}
                  onChange={(event) => {
                    moveMin(Number(event.target.value))
                  }}
                  step={1}
                  type="range"
                  value={minStep}
                />
                <input
                  aria-label={copy.ariaMax}
                  aria-valuetext={zone.max === null ? zoneText : formatMultiplier(zone.max)}
                  className="birdie-autobet-axis__range birdie-autobet-axis__range--max"
                  max={AXIS_STEPS}
                  min={0}
                  onChange={(event) => {
                    moveMax(Number(event.target.value))
                  }}
                  step={1}
                  type="range"
                  value={maxStep}
                />
                <div aria-hidden="true" className="birdie-autobet-axis__track">
                  <span
                    className="birdie-autobet-axis__band"
                    style={{
                      left: pct(minStep / AXIS_STEPS),
                      width: pct((maxStep - minStep) / AXIS_STEPS)
                    }}
                  />
                  {axisTiles.map((tile) => (
                    <span
                      className={`birdie-autobet-axis__tick${caughtIds.has(tile.optionId) ? ' birdie-autobet-axis__tick--caught' : ''}`}
                      key={tile.optionId}
                      style={{ left: pct(axisPos(tile.multiplier)) }}
                    />
                  ))}
                </div>
                {/* The handles the player sees: the board's own ball, sitting
                    in a gold cup ring at exactly the step its thumb is on. The
                    native thumbs above are transparent and keep every one of
                    their keyboard manners, focus ring included. */}
                <div aria-hidden="true" className="birdie-autobet-axis__handles">
                  <span
                    className={`birdie-autobet-axis__handle birdie-autobet-axis__handle--min${minOnTop ? ' birdie-autobet-axis__handle--front' : ''}`}
                    style={{ left: pct(minStep / AXIS_STEPS) }}
                  >
                    <GolfBall className="birdie-autobet-axis__ball" />
                  </span>
                  <span
                    className="birdie-autobet-axis__handle birdie-autobet-axis__handle--max"
                    style={{ left: pct(maxStep / AXIS_STEPS) }}
                  >
                    <GolfBall className="birdie-autobet-axis__ball" />
                  </span>
                </div>
                <div aria-hidden="true" className="birdie-autobet-axis__labels">
                  {AXIS_TICKS.map((tick) => (
                    <span
                      className="birdie-autobet-axis__label"
                      key={tick}
                      style={{ left: pct(axisPos(tick)) }}
                    >
                      {formatMultiplier(tick)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Markets: three equal tabs, each beside the board's own icon ── */}
            <div className="birdie-autobet-sheet__field" style={rise(1)}>
              <div className="birdie-autobet-sheet__label">
                <span>{copy.marketsLabel}</span>
              </div>
              <BirdieAutoBetSeg
                items={marketItems}
                label={copy.marketsLabel}
                onPick={(markets) => {
                  onDraftChange({ ...draft, markets })
                }}
                value={draft.markets}
              />
            </div>

            {/* ── The two repeats, as the board's own round tiles ── */}
            <div className="birdie-autobet-sheet__field" style={rise(2)}>
              <div className="birdie-autobet-sheet__label">
                <span>{copy.roundTypeTitle}</span>
              </div>
              <div className="birdie-autobet-sheet__marks">
                {ROUND_TYPES.map(({ side, kind }) => {
                  const pressed = draft.roundType[side]
                  return (
                    <button
                      aria-pressed={pressed}
                      className={`${on('birdie-autobet-mark', pressed)} birdie-autobet-mark--${side}`}
                      key={side}
                      onClick={() => {
                        onDraftChange({
                          ...draft,
                          roundType: { ...draft.roundType, [side]: !pressed }
                        })
                      }}
                      type="button"
                    >
                      {/* The lamp on the switch: dark and sunk when the repeat
                          is off, lit in the family's own colour when it is on.
                          The second, non-colour half of that answer is the
                          tile itself — recessed off, standing up on. */}
                      <span aria-hidden="true" className="birdie-autobet-mark__pip" />
                      {/* Keyed on the state so the mark REMOUNTS when the
                          repeat is pressed and its engage animation plays
                          again; a class alone would find the animation
                          already on the element and do nothing. */}
                      <RoundMark key={String(pressed)} kind={kind} size={MARK_PX} />
                      <span className="birdie-autobet-mark__text">{copy[side]}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* ── Stake: the board's own chips, landing in the zone that stakes everything ── */}
            <div className="birdie-autobet-sheet__field" style={rise(3)}>
              <div className="birdie-autobet-sheet__label">
                <span>{copy.stakePerOption}</span>
              </div>
              <div className="birdie-autobet-sheet__stake">
                <div
                  aria-label={copy.stakePerOption}
                  className="birdie-autobet-sheet__stake-chips"
                  role="radiogroup"
                >
                  {chipValues.map((value, index) => {
                    const style = chipStyleAt(index)
                    return (
                      <button
                        aria-checked={value === draft.stake}
                        className={on('birdie-autobet-chip', value === draft.stake)}
                        key={value}
                        onClick={() => {
                          pickStake(value)
                        }}
                        role="radio"
                        style={{
                          background: style.bg,
                          borderColor: style.border,
                          color: style.color
                        }}
                        type="button"
                      >
                        {formatNumberWithSuffix(value, t)}
                      </button>
                    )
                  })}
                </div>
                {/* The landed chip. Keyed on the stake so the pop replays on
                    every pick; the radio above is what a reader is told. */}
                <span aria-hidden="true" className="birdie-autobet-sheet__slot">
                  <span
                    className="birdie-autobet-chip birdie-autobet-chip--slot"
                    key={draft.stake}
                    style={{
                      background: stakeStyle.bg,
                      borderColor: stakeStyle.border,
                      color: stakeStyle.color
                    }}
                  >
                    {formatNumberWithSuffix(draft.stake, t)}
                  </span>
                </span>
              </div>
            </div>

            {/* ── Max per round · Stop after, one reading each ── */}
            <div className="birdie-autobet-sheet__row">
              <div className="birdie-autobet-sheet__field" style={rise(4)}>
                <div className="birdie-autobet-sheet__label">
                  <span>{copy.maxPerRound}</span>
                </div>
                <Stepper
                  label={copy.maxPerRound}
                  lessLabel={copy.fewerOptions}
                  moreLabel={copy.moreOptions}
                  onLess={stepOptionsTo(-1)}
                  onMore={stepOptionsTo(1)}
                  unlimited={false}
                  value={copy.nOptions(maxOptions)}
                />
              </div>

              <div className="birdie-autobet-sheet__field" style={rise(5)}>
                <div className="birdie-autobet-sheet__label">
                  <span>{copy.stopAfter}</span>
                </div>
                <Stepper
                  label={copy.stopAfter}
                  lessLabel={copy.fewerRounds}
                  moreLabel={copy.moreRounds}
                  onLess={stepTo(
                    stepRounds(draft.roundsLeft, -1),
                    draft.roundsLeft,
                    (roundsLeft) => {
                      onDraftChange({ ...draft, roundsLeft })
                    }
                  )}
                  onMore={stepTo(
                    stepRounds(draft.roundsLeft, 1),
                    draft.roundsLeft,
                    (roundsLeft) => {
                      onDraftChange({ ...draft, roundsLeft })
                    }
                  )}
                  unlimited={draft.roundsLeft === null}
                  value={
                    draft.roundsLeft === null ? copy.untilIStop : copy.nRounds(draft.roundsLeft)
                  }
                />
              </div>
            </div>

            {/* ── What a round can cost ───────────────────────────────────
                A worked example of the open round stood here: the tiles the
                rule would take, priced, under a waiting line between rounds.
                It was whichever round happened to be up — sometimes a dozen
                chips, sometimes nothing at all — and none of it said the one
                thing the player was reading for (owner 2026-09-17).

                It was then one quiet grey line, which is the other way to lose
                it (owner 2026-09-18: nobody should feel they staked a ton by
                overlooking how much they were actually staking). So it is a
                READOUT now, directly above the button that arms the rule: the
                same sentence, in the same sixteen locales, with the SUM cut out
                of it and set large in gold. The words keep their order — the
                cut is by position, so a locale that leads with the money leads
                with the money here too.

                The live region is the whole paragraph, as it was: the text it
                announces is character for character the sentence it announced
                before, so the ceiling stepper still says the count and then
                what it costs. */}
            <p aria-live="polite" className="birdie-autobet-sheet__ceiling">
              {ceilingLine.before === '' ? null : (
                <span className="birdie-autobet-sheet__ceiling-words">{ceilingLine.before}</span>
              )}
              {ceilingLine.match === '' ? null : (
                // Keyed on the reading, so the tick replays whenever the sum
                // itself changes and never when something else in the sheet does.
                <span className="birdie-autobet-sheet__ceiling-amount" key={ceilingLine.match}>
                  {ceilingLine.match}
                </span>
              )}
              {ceilingLine.after === '' ? null : (
                <span className="birdie-autobet-sheet__ceiling-words">{ceilingLine.after}</span>
              )}
            </p>

            {/*
              Armed, the sheet is still the rule's editor: every control above
              edits the draft, so the armed sheet needs a way to hand that
              draft to the running rule — otherwise the settings on screen are
              not the ones placing bets. Update re-arms (the machine forgets
              the planned race, so the open round is planned again under the
              new rule); Stop stays beside it.
            */}
            {armed ? (
              <div className="birdie-autobet-sheet__actions">
                <button className="birdie-autobet-sheet__arm" onClick={onUpdate} type="button">
                  {copy.update}
                </button>
                <button
                  className="birdie-autobet-sheet__arm birdie-autobet-sheet__arm--stop"
                  onClick={onStop}
                  type="button"
                >
                  {copy.stop}
                </button>
              </div>
            ) : (
              // SET UP, not start, when there is nothing to place on. `preview`
              // is null exactly while the round cannot be planned, which is
              // exactly when arming places nothing until the next round opens —
              // the board's opener is on screen only in that window, and a
              // button that said start there would promise a bet this round
              // (`autoBetCopy` says so of the opener's own label).
              <button className="birdie-autobet-sheet__arm" onClick={onStart} type="button">
                {preview === null ? copy.setUp : copy.start}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(sheet, document.body)
}
